import {
    Button,
    Divider,
    Popover,
    Stack,
    type ButtonProps,
    type MantineSize,
} from '@mantine-8/core';
import { useClickOutside, useDisclosure } from '@mantine-8/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { memo, useCallback, useEffect, useRef, useState, type FC } from 'react';
import MantineIcon from '../common/MantineIcon';
import AutoFetchResultsSwitch from './AutoFetchResultsSwitch';
import LimitInput from './LimitInput';
import PreAggregateCacheSwitch from './PreAggregateCacheSwitch';

export type Props = {
    size?: MantineSize;
    disabled?: boolean;
    maxLimit: number;
    limit: number;
    onLimitChange: (value: number) => void;
    showAutoFetchSetting?: boolean;
    showPreAggregateSetting?: boolean;
    targetProps?: ButtonProps;
};

const RunQuerySettings: FC<Props> = memo(
    ({
        size,
        disabled,
        maxLimit,
        limit,
        onLimitChange,
        showAutoFetchSetting = false,
        showPreAggregateSetting = false,
        targetProps,
    }) => {
        const [opened, { open, close }] = useDisclosure(false);
        const mouseDownInsideRef = useRef(false);
        const handleClickOutside = useCallback(() => {
            if (mouseDownInsideRef.current) {
                mouseDownInsideRef.current = false;
                return;
            }
            setTimeout(() => close(), 0);
        }, [close]);
        const ref = useClickOutside(handleClickOutside, [
            'mouseup',
            'touchend',
        ]);

        const [tempLimit, setTempLimit] = useState(limit);

        useEffect(() => {
            setTempLimit(limit);
        }, [limit]);

        const handleLimitChange = (value: number) => {
            setTempLimit(value);
        };

        const handleLimitBlur = () => {
            if (tempLimit !== limit) {
                onLimitChange(tempLimit);
            }
        };

        return (
            <Popover
                withinPortal
                disabled={disabled}
                opened={opened}
                position="bottom-end"
                withArrow
                shadow="md"
                offset={2}
                arrowOffset={10}
            >
                <Popover.Target>
                    <Button
                        size={size}
                        p="xs"
                        disabled={disabled}
                        onClick={opened ? undefined : open}
                        {...targetProps}
                    >
                        <MantineIcon icon={IconChevronDown} size="sm" />
                    </Button>
                </Popover.Target>

                <Popover.Dropdown>
                    <Stack
                        ref={ref}
                        onMouseDown={() => {
                            mouseDownInsideRef.current = true;
                        }}
                    >
                        {showPreAggregateSetting && (
                            <PreAggregateCacheSwitch size={size} />
                        )}
                        {showPreAggregateSetting && showAutoFetchSetting && (
                            <Divider />
                        )}
                        {showAutoFetchSetting && (
                            <AutoFetchResultsSwitch size={size} />
                        )}
                        {(showAutoFetchSetting || showPreAggregateSetting) && (
                            <Divider />
                        )}
                        <LimitInput
                            maxLimit={maxLimit}
                            limit={tempLimit}
                            onLimitChange={handleLimitChange}
                            size={size}
                            numberInputProps={{
                                onBlur: handleLimitBlur,
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        handleLimitBlur();
                                        close();
                                    }
                                },
                            }}
                        />
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        );
    },
);

export default RunQuerySettings;
